<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('common.works')"
      :grid-ref="$refs[workGridRefKey]"
      :show-add="false"
      :show-filter="false"
      :show-refresh="true"
    />
    <div class="content-body pt-1">
      <div class="row">
        <div id="toast-msg"></div>
      </div>
      <DxDataGrid
        :ref="workGridRefKey"
          :data-source="workDatasource"
          :selected-row-keys="selectedRowUuids"
          :column-min-width="110"
          style="min-height: 350px"
          :show-column-lines="true"
          :show-borders="true"
          :column-auto-width="true"
          :allowColumnReordering="true"
          :allowColumnResizing="true"
          :row-alternation-enabled="true"
          :remote-operations="gridRemoteOperations"
          :element-attr="gridAttributes"
          :allow-column-resizing="false"
          :on-row-prepared="rowMarker"
          :on-editor-preparing="onEditorPreparing"
          @editing-start="onEditingStart"
          @rowRemoving="onRowRemoving"
          @option-changed="onOptionChanged"
          @content-ready="onContentReady"
          @row-click="onRowClick"
          @selection-changed="onSelectionChanged"
        >
          <DxColumn
            data-field="work_started_at"
            data-type="date"
            :caption="$t('common.date')"
            format="yyyy-MM-dd"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            data-field="work.uuid"
            data-type="string"
            :caption="$t('common.work_type')"
            :editor-options="columnEditorOptions"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
            cell-template="workTypeViewTemplate"
            :calculateCellValue="getWorkTypeColumnText"
          >
            <DxLookup :data-source="workTypes" value-expr="uuid" display-expr="name"> </DxLookup>
          </DxColumn>
          <template #workTypeViewTemplate="{ data }">
            <span>
              {{ getWorkTypeColumnText(data.data.work?.uuid) }}
            </span>
          </template>
          <DxColumn
            data-field="machine.name"
            data-type="string"
            :caption="$t('common.tractor')"
            :editor-options="columnEditorOptions"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            data-field="machinery.name"
            data-type="string"
            :caption="$t('common.work_machine')"
            :editor-options="columnEditorOptions"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            data-field="person.name"
            data-type="string"
            :caption="$t('common.driver')"
            :editor-options="columnEditorOptions"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            data-field="season_uuid"
            data-type="string"
            :caption="$t('works.work_list.season')"
            :min-width="130"
            :editor-options="columnEditorOptions"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
            :allow-editing="false"
            cell-template="seasonViewTemplate"
            :calculateCellValue="getSeasonColumnText"
          >
            <DxLookup :data-source="seasonsList" value-expr="season_uuid" display-expr="name"> </DxLookup>
          </DxColumn>
          <template #seasonViewTemplate="{ data }">
            <span>
              {{ getSeasonColumnText(data.data.season?.uuid) }}
            </span>
          </template>
          <DxColumn
            data-field="parcel.name"
            data-type="string"
            :caption="$t('common.parcel')"
            :editor-options="columnEditorOptions"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            data-field="parcel.surface"
            data-type="number"
            :caption="$t('works.work_list.area')"
            :editor-options="columnEditorOptions"
            format="#,##0.## ha"
            :allow-editing="false"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            data-field="net_surface_worked"
            data-type="number"
            :caption="$t('works.work_list.worked_area')"
            :editor-options="columnEditorOptions"
            format="#,##0.## ha"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            data-field="notes"
            data-type="string"
            :caption="$t('remark')"
            :editor-options="columnEditorOptions"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            :width="60"
            alignment="center"
            :fixed="true"
            fixed-position="right"
            :allow-sorting="false"
            :allow-filtering="false"
            :show-in-column-chooser="false"
            cell-template="dynamicMenuCell"
          />
          <template #dynamicMenuCell="{ data }">
            <DxActionMenu
              :grid-ref="$refs[workGridRefKey]"
              row-key="uuid"
              :row="data.data"
              :can-edit="canEdit"
              :can-delete="canDelete"
              :allow-deleting="allowDeleting"
            />
          </template>
          <DxPaging :total="3500" :page-size="15" />
          <DxPager
            :visible="true"
            :allowed-page-sizes="pageSizes"
            display-mode="compact"
            :show-page-size-selector="true"
            :show-info="true"
            :show-navigation-buttons="true"
            :info-text="$t('common.page_info')"
          />
          <DxEditing :allow-adding="false" :allow-updating="false" :allow-deleting="false" :use-icons="true" mode="row">
            <DxTexts confirm-delete-message="Sigur vreți să ștergeți lucrarea?" , confirm-delete-title="Confirmare ștergere" />
          </DxEditing>
          <DxHeaderFilter :visible="true" />
          <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
          <DxSelection mode="multiple" showCheckBoxesMode="always" selectAllMode="page" />
          <DxScrolling column-rendering-mode="virtual" />
          <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
          <DxColumnChooser :enabled="false" mode="select" />
      </DxDataGrid>
      <GridGroupActions
        ref="groupActions"
        :grid-ref="$refs[workGridRefKey]"
        :selected-count="selectedRowUuidsAll.length"
        :show-delete-action="canDelete"
        delete-endpoint="/season-on-parcel-has-culture/bulkDelete"
        :delete-rows-by-uuid="selectedRowsByUuid"
        :allow-deleting="allowDeleting"
        :delete-uuids="selectedRowUuidsAll"
        @cleared="onSelectionCleared"
        @delete-success="onBulkDeleteSuccess"
      />
      <WorkEdit
        v-if="editingPopupVisible"
        :workData="editingRowData"
        :visible="editingPopupVisible"
        @close="
          editingPopupVisible = false;
          editingRowData = null;
        "
        @save="
          editingPopupVisible = false;
          editingRowData = null;
          $refs[workGridRefKey].instance.refresh();
        "
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  DxColumn,
  DxDataGrid,
  DxEditing,
  DxFilterRow,
  DxHeaderFilter,
  DxLookup,
  DxPager,
  DxPaging,
  DxRequiredRule,
  DxSelection,
  DxStringLengthRule,
  DxTexts,
  DxScrolling,
  DxPopup,
  DxGroupPanel,
  DxColumnChooser,
} from "devextreme-vue/data-grid";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import CustomStore from "devextreme/data/custom_store";
import notify from "devextreme/ui/notify";
import WorkEdit from "./WorkEdit.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import GridGroupActions from "@/components/DxGrid/GridGroupActions.vue";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";

export default {
  name: "WorkListDesktop",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      workTypes: [],
      seasonsList: [],
      gridAttributes: {
        class: "smagria-grid work-list-grid",
      },
      workGridRefKey: "workListGrid",
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 50, 100],
      columnEditorOptions: { showClearButton: true },
      works: [],
      selectedRowUuids: [],
      selectedRowUuidsAll: [],
      selectedRowsByUuid: {},
      grid_data_uuids: [],
      isRestoringSelection: false,
      workDatasource: new CustomStore({
        key: "uuid",
        load: this.load,
        remove: this.remove,
      }),
      biggest_year_uuid: null, // To store the biggest UUID based on year
      editingPopupVisible: false,
      editingRowData: null,
    };
  },
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxEditing,
    DxTexts,
    DxLookup,
    DxRequiredRule,
    DxStringLengthRule,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    Forbidden,
    DxScrolling,
    DxPopup,
    DxGroupPanel,
    DxColumnChooser,
    WorkEdit,
    GridPanelHeader,
    DxActionMenu,
    GridGroupActions,
  },
  computed: {
    config() {
      return {
        permission: permissions.WORK_LIST,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getWorks: "seasonOnParcelHasCulture/getSeasonOnParcelHasCulture",
      deleteWork: "seasonOnParcelHasCulture/deleteWork",
      getWorkTypes: "works-manager/getWorks",
      getSeasons: "seasons/getSeasons",
    }),
    allowUpdating(row) {
      // Allow editing if the row is not deleted and has no stock movement
      return this.canEdit && !row.has_batch_item && !row.has_stock_transaction;
    },
    allowDeleting(row) {
      // Hide delete icon if deleted_at is set
      return this.canDelete && !row.has_batch_item && !row.has_stock_transaction;
    },
    onEditingStart(e) {
      e.cancel = true;
      this.editingRowData = e.data; // Store the row data for editing

      // Check if the current row has has_batch_item
      if (e.data.has_batch_item || e.data.has_stock_transaction) {
        this.workNotify("This row cannot be edited as it has batch items.", "error");
      } else {
        this.editingPopupVisible = true;
      }
    },
    rowMarker(e) {
      if (e.rowType === "data" && (e.data.has_batch_item || e.data.has_stock_transaction)) {
        //e.rowElement.style.backgroundColor = ROW_BACKGROUND_COLOR;
      }
    },
    onEditorPreparing(e) {
      if (e.parentType === "dataRow" && e.dataField === "parcel.uuid") {
        const isSeasonNotSet = e.row.data.season?.uuid === undefined || e.row.data.season?.uuid === null;
        e.editorOptions.disabled = isSeasonNotSet;
      }
    },
    onRowRemoving(e) {
      if (e.data.has_batch_item || e.data.has_stock_transaction) {
        this.workNotify("Work cannot be deleted, work has input .", "error");
        e.cancel = true;
      } else {
        e.cancel = false;
      }
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        const instance = this.$refs?.[this.workGridRefKey]?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
        }
      }
    },
    syncSelectedRowsByUuid(items = []) {
      const selectedUuidSet = new Set(this.selectedRowUuidsAll);
      const nextSelectedRowsByUuid = {};

      Object.keys(this.selectedRowsByUuid).forEach((uuid) => {
        if (selectedUuidSet.has(uuid)) {
          nextSelectedRowsByUuid[uuid] = this.selectedRowsByUuid[uuid];
        }
      });

      (Array.isArray(items) ? items : []).forEach((item) => {
        if (item && selectedUuidSet.has(item.uuid)) {
          nextSelectedRowsByUuid[item.uuid] = item;
        }
      });

      this.selectedRowsByUuid = nextSelectedRowsByUuid;
    },
    async load(loadOptions) {
      let dataSource = {
        data: [],
        totalCount: 0,
      };

      let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
      let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

      let payload = {
        page: pageNumber,
        limit: pageLimit,
        filter: loadOptions.filter,
        sort: loadOptions.sort,
      };

      await this.getWorks(payload).then((resp) => {
        dataSource.data = resp.data;
        dataSource.totalCount = resp.pagination?.total ?? 1;
        this.grid_data_uuids = Array.isArray(resp.data) ? resp.data.map((item) => item.uuid) : [];
        this.syncSelectedRowsByUuid(resp.data);
      });

      return dataSource;
    },
    async remove(key, obj) {
      await this.deleteWork({
        uuid: key,
      })
        .then((response) => {
          this.workNotify("Work has been deleted.", "success");
        })
        .catch((e) => {
          this.workNotify("Error deleting work.", "error");
        });
    },
    workNotify(message, type) {
      notify(
        {
          message: message,
          width: "auto",
          position: {
            at: "center",
            my: "start",
            of: "#toast-msg",
          },
        },
        type,
        3000,
      );
    },
    getSeasonColumnText(season_uuid) {
      if (!season_uuid) return "-";
      const season = this.seasonsList.find((s) => s.season_uuid === season_uuid);
      return season ? season.name : "-";
    },
    getWorkTypeColumnText(work_type_uuid) {
      if (!work_type_uuid) return "-";
      const workType = this.workTypes.find((wt) => wt.uuid === work_type_uuid);
      return workType ? workType.name : "-";
    },
    onExporting(e) {
      e.fileName = "Work_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Work");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    onSelectionChanged(e) {
      this.selectedRowUuids = Array.isArray(e.selectedRowKeys) ? e.selectedRowKeys : [];

      if (this.isRestoringSelection) {
        return;
      }

      const instance = this.$refs?.[this.workGridRefKey]?.instance;
      const items = instance?.getDataSource()?.items?.() ?? [];
      const currentSelected = Array.isArray(e?.currentSelectedRowKeys) ? e.currentSelectedRowKeys : [];
      const currentDeselected = Array.isArray(e?.currentDeselectedRowKeys) ? e.currentDeselectedRowKeys : [];

      currentSelected.forEach((key) => {
        if (this.grid_data_uuids.includes(key) && !this.selectedRowUuidsAll.includes(key)) {
          this.selectedRowUuidsAll.push(key);
        }
      });

      currentDeselected.forEach((key) => {
        if (this.grid_data_uuids.includes(key)) {
          const index = this.selectedRowUuidsAll.indexOf(key);
          if (index !== -1) {
            this.selectedRowUuidsAll.splice(index, 1);
          }
        }
      });

      this.syncSelectedRowsByUuid(items);
    },
    onRowClick(e) {
      if (!e || !e.component || e.rowType !== "data") return;
      const key = e.key;
      const instance = e.component;
      if (instance.isRowSelected(key)) {
        instance.deselectRows([key]);
        return;
      }
      instance.selectRows([key], true);
    },
    onContentReady() {
      const instance = this.$refs?.[this.workGridRefKey]?.instance;
      if (instance) {
        const items = instance.getDataSource()?.items?.() ?? [];
        this.grid_data_uuids = Array.isArray(items) ? items.map((item) => item.uuid) : [];
        this.syncSelectedRowsByUuid(items);
      }
      this.restoreSelection();
    },
    restoreSelection() {
      const instance = this.$refs?.[this.workGridRefKey]?.instance;
      if (!instance) {
        return;
      }

      this.isRestoringSelection = true;
      this.selectedRowUuids = this.selectedRowUuidsAll.filter((uuid) => this.grid_data_uuids.includes(uuid));
      const items = instance.getDataSource()?.items?.() ?? [];
      this.syncSelectedRowsByUuid(items);
      instance.selectRows(this.selectedRowUuids, false);
      this.$nextTick(() => {
        this.isRestoringSelection = false;
      });
    },
    onSelectionCleared() {
      this.selectedRowUuids = [];
      this.selectedRowUuidsAll = [];
      this.selectedRowsByUuid = {};
      const instance = this.$refs?.[this.workGridRefKey]?.instance;
      if (instance) {
        instance.selectRows([], false);
      }
    },
    onBulkDeleteSuccess() {
      this.reloadGrid();
    },
    reloadGrid() {
      const instance = this.$refs?.[this.workGridRefKey]?.instance;
      if (instance) {
        instance.getDataSource()?.reload?.();
      }
    },
  },
  mounted() {
    if (!this.canView) {
      return;
    }

    this.getWorkTypes({
      limit: 10000,
    })
      .then((response) => {
        this.workTypes = response.data;
      })
      .catch((error) => {
        console.error("Error fetching work types:", error);
      });

    this.getSeasons({ limit: 1000 })
      .then((response) => {
        this.seasonsList = response.data.map((season) => ({
          season_uuid: season.uuid,
          name: season.name,
          year: season.year, // Assuming 'year' is a property in the season object
        }));

        // Find the biggest UUID (lexicographically)
        const maxYear = Math.max(...this.seasonsList.map((s) => s.year));
        this.biggest_year_uuid = this.seasonsList.find((s) => s.year == maxYear)?.season_uuid;

        this.$nextTick(() => {
          // If you want to filter the grid immediately after loading
          if (this.$refs[this.workGridRefKey] && this.$refs[this.workGridRefKey].instance) {
            //this.$refs.workListGrid.instance.filter([["season_uuid", "=", this.biggest_year_uuid]]);
            // Set the filter value for the column (shows in filter row UI)
            this.$refs[this.workGridRefKey].instance.columnOption("season_uuid", "filterValue", this.biggest_year_uuid);
          }
        });

        console.log("Seasons fetched successfully");
      })
      .catch((error) => {
        console.error("Error fetching seasons:", error);
      });
  },
};
</script>
